import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ButtonBase
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import { debounce } from 'lodash';
import { Cancel, Edit, Visibility } from '@mui/icons-material';

function ProdutoList() {
  const location = useLocation();
  const navigate = useNavigate();

  // Filtros
  const [nome, setNome] = useState('');
  const [cod, setCod] = useState('');
  const [codFabricante, setCodFabricante] = useState('');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [complemento, setComplemento] = useState('');

  // Venda Suspensa (Checkbox) => "S" se marcado, "" se desmarcado
  const [vendaSuspensa, setVendaSuspensa] = useState('');

  // Saldo (Radio) => "", "P", "Z"
  const [saldo, setSaldo] = useState('');

  const [tamanho, setTamanho] = useState('');
  const [idCorProduto, setIdCorProduto] = useState(0);
  const [subgrupo, setSubgrupo] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [ordenacao, setOrdenacao] = useState('C');
  const [status, setStatus] = useState(true);

  // Listagem e Paginação
  const [produtos, setProdutos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Config
  const registrosPorPagina = 10;
  const API_URL = '/api/EstProdutos';

  // Função que chama o backend
  const buscarProdutos = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          nome,
          cod,
          codFabricante,
          codigoBarras,
          complemento,
          vendaSuspensa,
          saldo,
          tamanho,
          idCorProduto,
          subgrupo,
          fabricante,
          ordenacao:'N',
          status,
          page: pagina - 1, // zero-based
          size: registrosPorPagina
        }
      });
      setProdutos(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas
  const debouncedBuscar = useCallback(
    debounce(() => {
      buscarProdutos();
    }, 500),
    [
      nome, cod, codFabricante, codigoBarras, complemento,
      vendaSuspensa, saldo, tamanho, idCorProduto,
      subgrupo, fabricante, ordenacao, status,
      pagina
    ]
  );

  // Efeito para disparar busca automática quando filtros ou pagina mudarem
  useEffect(() => {
    debouncedBuscar();
    return debouncedBuscar.cancel;
  }, [
    nome, cod, codFabricante, codigoBarras, complemento,
    vendaSuspensa, saldo, tamanho, idCorProduto,
    subgrupo, fabricante, ordenacao, status,
    pagina, debouncedBuscar
  ]);

  // Paginação
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Botão Limpar
  const limparFiltros = () => {
    setNome('');
    setCod('');
    setCodFabricante('');
    setCodigoBarras('');
    setComplemento('');
    setVendaSuspensa('');
    setSaldo('');
    setTamanho('');
    setIdCorProduto(0);
    setSubgrupo('');
    setFabricante('');
    setOrdenacao('N');
    setStatus(true);
    setPagina(1);
  };

  // Ações
  const handleNovo = () => {
    navigate('/produto/edit'); // Ajuste a rota
  };

  const handleEditar = (idProduto) => {
    navigate(`/produto/edit/${idProduto}`);
  };

  const handleExcluir = async (idProduto) => {
    console.log('Excluindo produto ID:', idProduto);
    // Exemplo:
    // await axios.delete(`${API_URL}/${idProduto}`);
    // buscarProdutos();
  };

  // Handler para checkbox de "Venda Suspensa"
  const handleVendaSuspensaChange = (e) => {
    if (e.target.checked) {
      setVendaSuspensa('S'); // Marcado => "S"
    } else {
      setVendaSuspensa('');  // Desmarcado => ""
    }
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop Carregamento */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título */}
      <Typography variant="h4" align="center" gutterBottom>
        Cadastro de Produtos
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2} alignItems="center">
            {/* Linha 1: Código, Nome, Complemento, Ordenação */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Código"
                variant="outlined"
                size="small"
                fullWidth
                value={cod}
                onChange={(e) => setCod(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Nome"
                variant="outlined"
                size="small"
                fullWidth
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Complemento"
                variant="outlined"
                size="small"
                fullWidth
                value={complemento}
                onChange={(e) => setComplemento(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ordenação</InputLabel>
                <Select
                  value={ordenacao}
                  label="Ordenação"
                  onChange={(e) => setOrdenacao(e.target.value)}
                >
                  <MenuItem value="C">Código</MenuItem>
                  <MenuItem value="N" defaultChecked>Nome</MenuItem>
                  {/* Adicione mais opções se quiser */}
                </Select>
              </FormControl>
            </Grid>

            {/* Linha 2: Cód. Fabricante, Cód. Barras, Tamanho, Fabricante */}
            <Grid item xs={12} sm={3}>
              <TextField
                label="Cód. Fabricante"
                variant="outlined"
                size="small"
                fullWidth
                value={codFabricante}
                onChange={(e) => setCodFabricante(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Cód. Barras"
                variant="outlined"
                size="small"
                fullWidth
                value={codigoBarras}
                onChange={(e) => setCodigoBarras(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Tamanho"
                variant="outlined"
                size="small"
                fullWidth
                value={tamanho}
                onChange={(e) => setTamanho(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Fabricante"
                variant="outlined"
                size="small"
                fullWidth
                value={fabricante}
                onChange={(e) => setFabricante(e.target.value)}
              />
            </Grid>

            {/* Linha 3: Saldo (radio), VendaSuspensa (checkbox), Subgrupo, Cor */}
            <Grid item xs={12} sm={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Saldo</FormLabel>
                <RadioGroup
                  row
                  value={saldo}
                  onChange={(e) => setSaldo(e.target.value)}
                >
                  <FormControlLabel value="" control={<Radio />} label="Todos" />
                  <FormControlLabel value="P" control={<Radio />} label="Positivo" />
                  <FormControlLabel value="Z" control={<Radio />} label="Zerado" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={vendaSuspensa === 'S'}
                    onChange={handleVendaSuspensaChange}
                  />
                }
                label="Venda Suspensa?"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Subgrupo"
                variant="outlined"
                size="small"
                fullWidth
                value={subgrupo}
                onChange={(e) => setSubgrupo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="ID Cor"
                variant="outlined"
                size="small"
                fullWidth
                value={idCorProduto}
                onChange={(e) => setIdCorProduto(e.target.value)}
              />
            </Grid>

            {/* Linha 4: Ativo, Botões */}
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={<Checkbox checked={status} onChange={(e) => setStatus(e.target.checked)} />}
                label="Ativo?"
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="outlined" onClick={limparFiltros} fullWidth>
                Limpar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

         <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
             <Button variant="contained" onClick={() => navigate('/produto/edit')}>
               + Novo
             </Button>
           </Box>

      {/* Tabela de Resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Cód</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Subgrupo</strong></TableCell>
              <TableCell><strong>Peso Bruto</strong></TableCell>
              <TableCell><strong>Saldo</strong></TableCell>
              <TableCell><strong>Saldo Encomenda</strong></TableCell>
              <TableCell><strong>Promo 1</strong></TableCell>
              <TableCell><strong>Promo 2</strong></TableCell>
              <TableCell><strong>Preço Vista</strong></TableCell>
              <TableCell><strong>Preço Prazo</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {produtos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Nenhum produto encontrado.
                </TableCell>
              </TableRow>
            ) : (
              produtos.map((prod, index) => (
                <TableRow key={index}>
                  <TableCell>{prod.codigoProduto}</TableCell>
                  <TableCell>{prod.descricao}</TableCell>
                  <TableCell>{prod.subgrupoProduto?.descricao}</TableCell>
                  <TableCell>{prod.pesoBruto}</TableCell>
                  <TableCell>{prod.quantidadeSaldoAtual || 0}</TableCell>
                  <TableCell>
                    {prod.saldoEncomenda} / {prod.saldoEncomendaReserva}
                  </TableCell>
                  <TableCell>{prod.promocao1 || 0}</TableCell>
                  <TableCell>{prod.promocao2 || 0}</TableCell>
                  <TableCell>
                    {prod.produtoPrecificacao?.precoVistaVarejo || 0}
                  </TableCell>
                  <TableCell>
                    {prod.produtoPrecificacao?.precoPrazoVarejo || 0}
                  </TableCell>
                 <TableCell sx={{ py: 0.5 }}>
                                     <Box
                                       sx={{
                                         display: 'flex',
                                         flexDirection: 'row',
                                         justifyContent: 'center',
                                         alignItems: 'center',
                                         gap: 2,
                                       }}
                                     >
                                       {/* Botão Editar: ícone e texto clicáveis */}
                                       <ButtonBase
                                         onClick={() => navigate(`/produto/edit/${prod.id}`)}
                                         sx={{
                                           display: 'flex',
                                           flexDirection: 'row', // força que seja sempre linha
                                           justifyContent: 'center',
                                           alignItems: 'center',
                                           p: 1,
                                         }}
                                       >
                                         <Edit fontSize="small" sx={{ color: 'orange' }} /> <br />
                                         <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                                           EDITAR
                                         </Typography>
                                       </ButtonBase>
                 
                                       {/* Botão Consultar */}
                                       <ButtonBase
                                         onClick={() => navigate(`/produto/view/${prod.id}`)}
                                         sx={{
                                           display: 'flex',
                                           flexDirection: 'column',
                                           alignItems: 'center',
                                           p: 1,
                                         }}
                                       >
                                         <Visibility fontSize="small" sx={{ color: 'blue' }} />
                                         <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                                           CONSULTAR
                                         </Typography>
                                       </ButtonBase>
                 
                                       {/* Botão Deletar */}
                                       <ButtonBase
                                         //   onClick={() => confirmDelete(cliente.id)}
                                         sx={{
                                           display: 'flex',
                                           flexDirection: 'column',
                                           alignItems: 'center',
                                           p: 1,
                                         }}
                                       >
                                         <Cancel fontSize="small" sx={{ color: 'red' }} />
                                         <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                                           DELETAR
                                         </Typography>
                                       </ButtonBase>
                                     </Box>
                                   </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default ProdutoList;
